import React, { createContext, useMemo, useState } from 'react'
import { isProviderACompany, isProviderAPos } from '../../../services/Provider/Provider.service'
import { can as canRoleService } from '../../../services/Role/role.service'

const UserContext = createContext({})
const UserProvider = ({children, baseUser = {}}) => {
    const [user, setUser] = useState(baseUser)

    user.getFirstPosFromLoginContext = () => {
        if (isProviderAPos(user.provider)) {
            return user.provider.object.id
        }

        const providers = user.loginContext.providers

        if (isProviderACompany(user.provider)) {
            const [first] = providers
              .filter(p => isProviderAPos(p) && p.object.company.id === user.provider.object.id)
              .sort((a, b) => a.object.name.localeCompare(b.object.name))

            return first?.object.id
        }

        if (user.loginContext?.type === 'OWN_SCOPE') {
            const [first] = providers
              .filter(isProviderAPos)
              .sort((a, b) => a.object.name.localeCompare(b.object.name))

            return first?.object.id
        }

        return null
    }

    user.getUserCompany = () => {
        return user.provider.type === 'COMPANY' ? user.provider.object : user.provider.object.company
    }

    user.getUserPos = () => {
        return isProviderAPos(user.provider) ? user.provider.object : null
    }

    user.getCompanyCurrency = () => {
        if (user.loginContext.type === 'OWN_SCOPE') {
            return 'EUR'
        }

        return user.provider.type === 'COMPANY' ?
          user.provider.object.country.currencyCode :
          user.provider.object.company.country.currencyCode
    }

    user.getCompanyLocale = () => {
        if (user.loginContext.type === 'OWN_SCOPE') {
            return 'fr-FR'
        }

        return user.provider.type === 'COMPANY' ?
          user.provider.object.country.locale :
          user.provider.object.company.country.locale
    }

    user.getCompanyCountry = () => {
        if (user.loginContext.type === 'OWN_SCOPE') {
            return 'FR'
        }

        return user.provider.type === 'COMPANY' ?
          user.provider.object.country.code :
          user.provider.object.company.country.code
    }

    user.isAdmin = () => {
        return user.provider?.role.name === 'POWER_USER'
    }

    user.isLoggedInOwnScope = user.loginContext?.type === 'OWN_SCOPE'

    const canCreateCategories = () => {
        if (user.provider.type === 'COMPANY') {
            return true
        }

        const [provider] = user.providers.filter(p => p.id === user.provider.id)
        return provider?.object?.canCreateCategories
    }

    const canExtendCategories = () => {
        if (user.provider.type === 'COMPANY') {
            return true
        }

        return user.provider?.object?.canExtendCategories
    }

    const can = (action, resource) => canRoleService(user, action, resource)

    const activeVariant = useMemo(() => {
        return user?.variant ?? undefined
    }, [user])

    const isLoggedIn = useMemo(() => {
        return !!user && !!user.id
    }, [user])

    // Define landing route based on role

    const landingRoute = (roleName = null) => {
        switch (roleName || user.provider?.role.name) {
            case 'ORDER_PREPARER':
                return '/orders/current'
            default:
                return '/analytics'
        }
    }

    return (
      <UserContext.Provider
        value={{user, isLoggedIn, setUser, can, landingRoute, canCreateCategories, canExtendCategories, activeVariant}}>
          {children}
      </UserContext.Provider>
    )
}

export { UserProvider, UserContext }
